import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CheckCircle2, Lock, CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PROJECT_TYPES, BUDGETS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const QuoteForm = ({ variant = "full", preselectService = "", source = "contact_page" }) => {
  const mini = variant === "mini";
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    project_type: preselectService,
    city: "",
    budget: "",
    start_date: "",
    details: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [dateOpen, setDateOpen] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = "Please enter your name.";
    if (!form.phone.trim()) er.phone = "Please enter your phone number.";
    if (!form.email.trim()) er.email = "Please enter your email.";
    else if (!emailRe.test(form.email)) er.email = "Please enter a valid email.";
    if (!mini && !form.project_type) er.project_type = "Please choose a project type.";
    if (!mini && !form.city.trim()) er.city = "Please enter your city.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, source });
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="text-center py-8" data-testid="quote-success">
        <div className="flex justify-center mb-4">
          <CheckCircle2 size={54} strokeWidth={1.5} style={{ color: "var(--gold)" }} />
        </div>
        <h3 className="h3" style={{ color: mini ? "var(--text-light)" : "var(--text-dark)" }}>
          Thanks — we'll call you within one business day.
        </h3>
        <p className="mt-3" style={{ color: mini ? "rgba(245,242,236,0.7)" : "var(--text-muted)", fontSize: "15px" }}>
          Your request is in. We'll be in touch with next steps and answers to any questions.
        </p>
      </div>
    );
  }

  const labelStyle = mini ? { color: "rgba(245,242,236,0.7)" } : {};
  const inputCls = `field-input ${mini ? "on-dark" : ""}`;
  const selectCls = `field-select ${mini ? "on-dark" : ""}`;

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="space-y-4">
      <div>
        <label className="field-label" style={labelStyle}>Full Name *</label>
        <input className={inputCls} value={form.full_name} onChange={set("full_name")} data-testid="qf-name" autoComplete="name" />
        {errors.full_name && <div className="field-error" data-testid="qf-name-error">{errors.full_name}</div>}
      </div>

      <div className={mini ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label className="field-label" style={labelStyle}>Phone *</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="qf-phone" autoComplete="tel" inputMode="tel" />
          {errors.phone && <div className="field-error" data-testid="qf-phone-error">{errors.phone}</div>}
        </div>
        <div className={mini ? "mt-4" : ""}>
          <label className="field-label" style={labelStyle}>Email *</label>
          <input className={inputCls} value={form.email} onChange={set("email")} data-testid="qf-email" autoComplete="email" inputMode="email" />
          {errors.email && <div className="field-error" data-testid="qf-email-error">{errors.email}</div>}
        </div>
      </div>

      {!mini && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Project Type *</label>
            <select className={selectCls} value={form.project_type} onChange={set("project_type")} data-testid="qf-project-type">
              <option value="">Select…</option>
              {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.project_type && <div className="field-error" data-testid="qf-project-type-error">{errors.project_type}</div>}
          </div>
          <div>
            <label className="field-label">City *</label>
            <input className={inputCls} value={form.city} onChange={set("city")} data-testid="qf-city" placeholder="e.g. Holmdel" autoComplete="address-level2" />
            {errors.city && <div className="field-error" data-testid="qf-city-error">{errors.city}</div>}
          </div>
        </div>
      )}

      {!mini && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Budget Range</label>
            <select className={selectCls} value={form.budget} onChange={set("budget")} data-testid="qf-budget">
              <option value="">Select…</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Preferred Start Date</label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={inputCls}
                  data-testid="qf-start-date"
                  style={{ paddingLeft: 42, position: "relative", textAlign: "left", display: "flex", alignItems: "center", cursor: "pointer" }}
                >
                  <CalendarDays size={18} strokeWidth={1.75} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--cta)", pointerEvents: "none" }} />
                  <span style={{ color: form.start_date ? "var(--text-dark)" : "var(--text-muted)" }}>
                    {form.start_date ? format(new Date(form.start_date + "T00:00:00"), "MMMM d, yyyy") : "Pick a date"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="aim-calendar-pop p-0" align="start" data-testid="qf-calendar">
                <Calendar
                  mode="single"
                  selected={form.start_date ? new Date(form.start_date + "T00:00:00") : undefined}
                  onSelect={(d) => {
                    setForm((f) => ({ ...f, start_date: d ? format(d, "yyyy-MM-dd") : "" }));
                    setErrors((er) => ({ ...er, start_date: undefined }));
                    setDateOpen(false);
                  }}
                  disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                  modifiersStyles={{
                    selected: { backgroundColor: "var(--cta)", color: "#17130a", fontWeight: 700 },
                    today: { color: "var(--cta)", fontWeight: 700 },
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      <div>
        <label className="field-label" style={labelStyle}>{mini ? "What do you need?" : "Project Details"}</label>
        <textarea className={`field-textarea ${mini ? "on-dark" : ""}`} value={form.details} onChange={set("details")} data-testid="qf-details" style={mini ? { minHeight: 90 } : {}} />
      </div>

      {mini && preselectService && (
        <input type="hidden" value={form.project_type} readOnly />
      )}

      {status === "error" && (
        <div className="field-error" data-testid="qf-submit-error">
          Something went wrong sending your request. Please call us at (908) 415-7371.
        </div>
      )}

      <div className="flex items-center gap-2" data-testid="qf-secure-note" style={{ color: mini ? "rgba(245,242,236,0.6)" : "var(--text-muted)", fontSize: 13 }}>
        <Lock size={14} strokeWidth={1.75} style={{ color: "var(--cta)", flexShrink: 0 }} />
        Your details are 100% secure — never shared with third-party companies.
      </div>

      <button type="submit" className="btn btn-primary btn-block" disabled={status === "loading"} data-testid="qf-submit">
        {status === "loading" ? "Sending…" : "Get My Free Estimate"}
      </button>
    </form>
  );
};
